/**
 * Google Tag Manager — central data-layer client.
 *
 * Scope of this file (Phase 1):
 *   • Initialise `window.dataLayer` defensively (the GTM snippet in
 *     `index.html` already creates it, but this guard keeps the helper
 *     usable in any future SSR/test scenario).
 *   • Provide a single `pushPageView()` entry point with built-in
 *     deduplication so React route changes never emit double events.
 *   • Provide a generic `pushEvent()` escape hatch for future marketing
 *     events (package_view, enquiry_submitted, whatsapp_click, etc.) —
 *     deliberately unused today per product brief.
 *
 * Intentionally NOT implemented yet (managed by marketing via GTM UI):
 *   Meta Pixel, GA4, Google Ads, conversion/ecommerce/lead tracking.
 */

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return null;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

// Module-level dedupe key: last successful page_view payload signature.
// Survives route changes within a single SPA session and is reset on
// hard reload (intended — a hard reload IS a new page_view).
let _lastPageViewKey = null;

const buildPageViewKey = (path, title) => `${path}::${title || ''}`;

/**
 * Push a `page_view` to the GTM data layer.
 *
 * @param {object} [opts]
 * @param {string} [opts.path]   override window.location.pathname
 * @param {string} [opts.title]  override document.title
 * @param {string} [opts.url]    override window.location.href
 * @returns {boolean} true if pushed, false if deduped/skipped.
 */
export const pushPageView = (opts = {}) => {
  const dl = ensureDataLayer();
  if (!dl) return false;

  const path  = opts.path  ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const title = opts.title ?? (typeof document !== 'undefined' ? document.title : '');
  const url   = opts.url   ?? (typeof window !== 'undefined' ? window.location.href : '');

  const key = buildPageViewKey(path, title);
  if (key === _lastPageViewKey) return false; // dedupe identical consecutive views
  _lastPageViewKey = key;

  dl.push({
    event: 'page_view',
    page_title: title,
    page_path: path,
    page_url: url,
  });
  return true;
};

/**
 * Generic event push for future marketing tags. Always merges
 * `{ event }` first so GTM triggers fire reliably.
 *
 * @param {string} eventName    e.g. 'package_view', 'whatsapp_click'
 * @param {object} [payload]    additional context (will be spread)
 * @returns {boolean}
 */
export const pushEvent = (eventName, payload = {}) => {
  if (!eventName || typeof eventName !== 'string') return false;
  const dl = ensureDataLayer();
  if (!dl) return false;
  dl.push({ event: eventName, ...payload });
  return true;
};

/**
 * Test/diagnostic helper — never used in production code paths.
 * Lets QA reset the dedupe key between scripted navigations.
 */
export const _resetPageViewDedupe = () => { _lastPageViewKey = null; };
